import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpParams,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, BehaviorSubject, from, throwError, interval, forkJoin } from 'rxjs';
import { environment } from 'src/environments/environment';
import { errorHttpReal, sinInterceptorGlobal } from 'src/app/shared/utils/error-http-real';
import { switchMap, catchError, finalize, map } from 'rxjs/operators';

// Modelo basado en la entidad del backend
export type InternalStatusEnum =
  | 'pendiente'
  | 'confirmada'
  | 'abonada'
  | 'rechazado'
  | 'error_deposito'
  | 'esperando_confirmacion';

export interface BinanceDeposit {
  id: string;
  binanceId: string;
  userId: string;
  txId: string;
  coin: string;
  network: string;
  amount: number;
  address: string;
  addressTag?: string;
  binanceStatus: string;
  internalStatus?: InternalStatusEnum;
  abonada: boolean;
  insertTime: string;
  completeTime?: string;
  transferType?: number;
  confirmTimes?: string;
  unlockConfirm?: number;
  walletType?: number;
  createdAt: string;
  updatedAt: string;
}

export interface QueryBinanceDepositDto {
  coin?: string;
  status?: number;
  startTime?: number;
  endTime?: number;
  offset?: number;
  limit?: number;
  txId?: string;
  includeSource?: boolean;
  userId?: string;
  binanceId?: string;
  address?: string;
  network?: string;
  page?: number;
}

export interface WalletCardexUser {
  id: string;
  userId: string;
  txId: string;
  binanceId: string;
  coin: string;
  network: string;
  amount: number;
  description?: string;
  debe: number;
  haber: number;
  saldo: number;
  createdAt: string;
  updatedAt: string;
}

export interface CardexResponse {
  items: WalletCardexUser[];
  total: number;
  page: number;
  limit: number;
  message?: string;
}

export interface UserBalanceResponse {
  userId: string | null;
  saldo: number;
  message?: string;
}

export interface UsdtToClpConversionDto {
  userId: string;
  usdtAmount: number;
  clpRate: number;
  clpAmount: number;
}

export interface UsdtToVesSwapDto {
  userId: string;
  usdtAmount: number;
  vesRate: number;
  vesAmount: number;
  cuentaBeneficiarioId?: number;
  destinoWallet?: boolean;
}

export interface SwapResponse {
  success: boolean;
  message: string;
  swap: {
    transactionId: string;
    userId: string;
    usdtAmount: number;
    vesAmount: number;
    rate: number;
    newUsdtBalance: number;
    beneficiario: {
      nombre: string;
      banco: string;
      numeroCuenta: string;
    };
  };
}

export interface ConversionResponse {
  success: boolean;
  message: string;
  conversion: {
    transactionId: string;
    userId: string;
    usdtAmount: number;
    clpAmount: number;
    rate: number;
    newUsdtBalance: number;
  };
}

export interface RetiroUsdtClpVesDto {
  userId: string;
  usdtAmount: number;
  clpAmount: number;
  tasa: number;
  cuentaRetiroId: number;
  tasaCambioId?: number;
  valorCambioId?: number;
}

export interface RetiroUsdtClpVesResponse {
  message: string;
  transaccionId: number;
  referencia: string;
  userId: string;
  userName: string;
  montoUsdt: number;
  montoClp: number;
  tasa: number;
  cuentaRetiro: {
    id: number;
    numeroCuenta: string;
    alias: string;
    banco: string;
  };
  balance: {
    saldoAnterior: number;
    saldoActual: number;
  };
  estado: string;
}

export interface WalletBalancesQueryDto {
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  userId?: string;
  coin?: string;
}


export interface WalletBalancesSummary {
  totalUsers: number;
  totalUsdtAbonados: number;
  totalUsdtDebitados: number;
  totalSaldoDisponible: number;
}

export interface WalletBalancesResult {
  users: UserWalletBalance[];
  summary: WalletBalancesSummary;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: {
    startDate?: string;
    endDate?: string;
    userId?: string;
    coin: string;
  };
}

export interface UserWalletBalance {
  userId: string;
  userName: string;
  userEmail: string;
  usdtAbonados: number;
  usdtDebitados: number;
  saldoDisponible: number;
  lastMovementDate?: Date;
}

// ========================
// INTERFACES PARA OPERACIONES DIARIAS
// ========================

/**
 * Resumen de conversiones USDT a CLP
 */
export interface ConversionsSummary {
  count: number;
  totalUsdtConverted: number;
  totalClpGenerated: number;
}

/**
 * Resumen de depósitos USDT
 */
export interface DepositsSummary {
  count: number;
  totalAmount: number;
}

/**
 * Resumen de retiros por estado específico
 */
export interface WithdrawalStatusSummary {
  count: number;
  totalAmount: number;
}

/**
 * Retiros agrupados por estado
 */
export interface WithdrawalsByStatus {
  pending: WithdrawalStatusSummary;
  completed: WithdrawalStatusSummary;
  rejected: WithdrawalStatusSummary;
  cancelled: WithdrawalStatusSummary;
}

/**
 * Resumen de retiros USDT del día
 */
export interface WithdrawalsSummary {
  byStatus: WithdrawalsByStatus;
  total: WithdrawalStatusSummary;
}

/**
 * Resumen completo de operaciones diarias
 */
export interface DailyOperationsSummary {
  date: string;
  conversions: ConversionsSummary;
  deposits: DepositsSummary;
  withdrawals: WithdrawalsSummary;
}

/**
 * Resumen de retiros pendientes de aprobación
 */
export interface PendingWithdrawalsForApproval {
  count: number;
  totalAmount: number;
}

/** Error del swap USDT→VES: el `Error` de siempre más lo que hace falta para clasificarlo. */
export interface ErrorSwapUsdtVes extends Error {
  /** Status HTTP, si se conoce. */
  status?: number;
  /**
   * El resultado del envío es incierto: sin conexión, timeout, o 5xx / error
   * aplanado SIN mensaje propio del API. Ahí el swap pudo haberse creado.
   */
  transitorio: boolean;
}

/**
 * Traduce el error de `POST binance-deposits/swap-usdt-to-ves`.
 *
 * La llamada va con `X-Skip-Error-Interceptor`, así que llega el error real
 * con su status (el 400 de "la tasa cambió mientras confirmabas", un 403…).
 * La clasificación es la compartida (`errorHttpReal`): incierto sólo sin
 * conexión, timeout o 5xx sin mensaje del API; un 4xx o un mensaje real del
 * servidor se muestra y se puede reintentar. Los textos por defecto son los
 * de `handleError` (`message` de class-validator unido con un espacio).
 */
export function errorSwapUsdtVes(error: any): ErrorSwapUsdtVes {
  const FALLBACK = 'Error desconocido';
  // Swap y retiro mueven dinero: un 5xx es incierto aunque traiga mensaje.
  const real = errorHttpReal(error, FALLBACK, { conEscape: true, mueveDinero: true });
  let msg = real.message;
  if (msg === FALLBACK && real.status === 0) msg = 'No se pudo conectar al servidor';

  const e = new Error(msg) as ErrorSwapUsdtVes;
  e.status = real.status;
  e.transitorio = real.transitorio;
  return e;
}

/**
 * Texto para el cliente cuando la confirmación del depósito quedó sin respuesta
 * clara (sin conexión, timeout o 5xx). No invita a reintentar a ciegas: si el
 * depósito sí quedó abonado, ya aparece en los movimientos.
 */
export const TEXTO_CONFIRMAR_DEPOSITO_INCIERTO =
  'No pudimos confirmar tu depósito en este momento. Revisa tus movimientos antes de volver a intentarlo; si no aparece, escríbenos a soporte con el TXID.';

/** Error de `POST binance-deposits/confirm` con su status real. */
export interface ErrorConfirmarDeposito extends Error {
  status?: number;
}

/**
 * Traduce el error de `POST binance-deposits/confirm` (llamada con
 * `X-Skip-Error-Interceptor`). Los 4xx del API (400 de validación, 404 no
 * existe, 409 ya asignado o fuera de la ventana de 72 h, 429 por intentos
 * fallidos) se muestran con su `message` real; sin conexión, timeout o 5xx
 * (aunque traiga mensaje) muestran `TEXTO_CONFIRMAR_DEPOSITO_INCIERTO`.
 */
export function errorConfirmarDeposito(error: any): ErrorConfirmarDeposito {
  const real = errorHttpReal(error, TEXTO_CONFIRMAR_DEPOSITO_INCIERTO, { conEscape: true });
  const es5xx = typeof real.status === 'number' && real.status >= 500;
  const msg = real.status === 0 || es5xx ? TEXTO_CONFIRMAR_DEPOSITO_INCIERTO : real.message;
  const e = new Error(msg) as ErrorConfirmarDeposito;
  e.status = real.status;
  return e;
}

@Injectable({ providedIn: 'root' })
export class BinanceDepositService {
  private apiUrl = `${environment.apiNest}binance-deposits`;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(private http: HttpClient) {}

  private getToken(): string {
    // Implementa la obtención del token según tu app
    return localStorage.getItem('token') || '';
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${this.getToken()}`);
  }

  private handleError(error: HttpErrorResponse) {
    let msg = 'Error desconocido';
    if (error.error?.message) {
      msg = error.error.message;
    } else if (error.status === 0) {
      msg = 'No se pudo conectar al servidor';
    } else if (error.status === 401) {
      msg = 'No autorizado';
    }
    return throwError(() => new Error(msg));
  }

  findAll(query?: QueryBinanceDepositDto): Observable<{
    items: BinanceDeposit[];
    total: number;
    offset: number;
    limit: number;
  }> {
    this.loadingSubject.next(true);
    let params = new HttpParams();
    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params = params.set(key, String(value));
        }
      });
    }
    return this.http
      .get<{
        items: BinanceDeposit[];
        total: number;
        offset: number;
        limit: number;
      }>(this.apiUrl, {
        headers: this.getHeaders(),
        params,
      })
      .pipe(
        catchError(this.handleError),
        finalize(() => this.loadingSubject.next(false)),
      );
  }

  confirmDeposit(data: {
    userId: string;
    txId: string;
    network: string;
    /** Solo operador («Asignar a cliente»): va al log del API, no al cliente. */
    motivo?: string;
    /** Solo operador: `id` de la fila; el API rechaza (409) si el txId es de otro depósito. */
    depositId?: string;
  }): Observable<any> {
    this.loadingSubject.next(true);
    return this.http
      .post<any>(`${this.apiUrl}/confirm`, data, {
        // Sin el `ErrorInterceptor`: aplanaba 404/409 y el cliente veía
        // «Error desconocido» en vez del mensaje del API.
        headers: sinInterceptorGlobal(this.getHeaders()),
      })
      .pipe(
        catchError((e) => throwError(() => errorConfirmarDeposito(e))),
        finalize(() => this.loadingSubject.next(false)),
      );
  }

  getUserCardex(
    userId?: string,
    page: number = 1,
    limit: number = 20,
  ): Observable<CardexResponse> {
    const resolvedUserId = userId || localStorage.getItem('userId') || '';
    const params = {
      userId: resolvedUserId,
      page: String(page),
      limit: String(limit),
    };
    return this.http.get<CardexResponse>(`${this.apiUrl}/cardex`, {
      headers: this.getHeaders(),
      params,
    });
  }

  getUserBalance(userId?: string): Observable<UserBalanceResponse> {
    const resolvedUserId = userId || localStorage.getItem('userId') || '';
    return this.http.get<UserBalanceResponse>(`${this.apiUrl}/saldo`, {
      headers: this.getHeaders(),
      params: { userId: resolvedUserId },
    });
  }

  getBalanceUpdates(userId?: string): Observable<number> {
    return interval(30000).pipe(
      // Check balance every 30 seconds
      switchMap(() => this.getUserBalance(userId)),
      map((response) => response.saldo),
    );
  }

  convertUsdtToClp(
    conversionData: UsdtToClpConversionDto,
  ): Observable<ConversionResponse> {
    this.loadingSubject.next(true);
    return this.http
      .post<ConversionResponse>(
        `${this.apiUrl}/convert-usdt-to-clp`,
        conversionData,
        {
          headers: this.getHeaders(),
        },
      )
      .pipe(
        catchError(this.handleError),
        finalize(() => this.loadingSubject.next(false)),
      );
  }

  /**
   * Igual que `convertUsdtToClp`, pero con el error real (escape del
   * `ErrorInterceptor` + `errorSwapUsdtVes`): el 400 «La tasa cambió…» y el
   * 503 «no hay tasa» llegan con su status (antes, el 503 se veía como «No
   * autorizado»), y un 5xx sin respuesta clara queda `transitorio`.
   */
  convertUsdtToClpConErrorReal(
    conversionData: UsdtToClpConversionDto,
  ): Observable<ConversionResponse> {
    this.loadingSubject.next(true);
    return this.http
      .post<ConversionResponse>(`${this.apiUrl}/convert-usdt-to-clp`, conversionData, {
        headers: sinInterceptorGlobal(this.getHeaders()),
      })
      .pipe(
        catchError((e) => throwError(() => errorSwapUsdtVes(e))),
        finalize(() => this.loadingSubject.next(false)),
      );
  }

  retiroUsdtClpVes(
    data: RetiroUsdtClpVesDto,
  ): Observable<RetiroUsdtClpVesResponse> {
    this.loadingSubject.next(true);
    return this.http
      .post<RetiroUsdtClpVesResponse>(
        `${this.apiUrl}/retiro-usdt-clp-ves`,
        data,
        {
          headers: this.getHeaders(),
        },
      )
      .pipe(
        catchError(this.handleError),
        finalize(() => this.loadingSubject.next(false)),
      );
  }

  /**
   * Igual que `retiroUsdtClpVes`, pero con el error real (escape del
   * `ErrorInterceptor` + `errorSwapUsdtVes`): un 5xx sin mensaje, status 0 o
   * timeout queda `transitorio` (envío incierto) y un 4xx o un mensaje real
   * del servidor se muestra. Lo usa el cotizador unificado; la pantalla
   * vieja sigue con `retiroUsdtClpVes` sin cambios.
   */
  retiroUsdtClpVesConErrorReal(
    data: RetiroUsdtClpVesDto,
  ): Observable<RetiroUsdtClpVesResponse> {
    this.loadingSubject.next(true);
    return this.http
      .post<RetiroUsdtClpVesResponse>(`${this.apiUrl}/retiro-usdt-clp-ves`, data, {
        headers: sinInterceptorGlobal(this.getHeaders()),
      })
      .pipe(
        catchError((e) => throwError(() => errorSwapUsdtVes(e))),
        finalize(() => this.loadingSubject.next(false)),
      );
  }

  swapUsdtToVes(swapData: UsdtToVesSwapDto): Observable<SwapResponse> {
    this.loadingSubject.next(true);
    return this.http
      .post<SwapResponse>(`${this.apiUrl}/swap-usdt-to-ves`, swapData, {
        // Sin el `ErrorInterceptor`: hace falta el status y el mensaje reales.
        headers: sinInterceptorGlobal(this.getHeaders()),
      })
      .pipe(
        // Handler propio (no `handleError`): conserva el status para que el
        // remittance-hub pueda marcar el envío como ambiguo.
        catchError((e) => throwError(() => errorSwapUsdtVes(e))),
        finalize(() => this.loadingSubject.next(false)),
      );
  }

  // ========================
  // MÉTODOS DE SALDOS DE WALLET
  // ========================

  /**
   * Obtener saldos de usuarios del cardex wallet
   */
  getWalletBalances(
    query?: WalletBalancesQueryDto,
  ): Observable<WalletBalancesResult> {
    this.loadingSubject.next(true);
    let params = new HttpParams();

    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params = params.set(key, String(value));
        }
      });
    }

    return this.http
      .get<WalletBalancesResult>(`${this.apiUrl}/wallet-balances`, {
        headers: this.getHeaders(),
        params,
      })
      .pipe(
        catchError(this.handleError),
        finalize(() => this.loadingSubject.next(false)),
      );
  }


  /**
   * Exportar saldos de wallet a Excel
   */
  exportWalletBalancesToExcel(
    query?: WalletBalancesQueryDto,
  ): Observable<Blob> {
    let params = new HttpParams();

    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params = params.set(key, String(value));
        }
      });
    }

    return this.http
      .get(`${this.apiUrl}/wallet-balances/export`, {
        headers: this.getHeaders(),
        params,
        responseType: 'blob',
      })
      .pipe(catchError(this.handleError));
  }


  /**
   * Descargar archivo Excel de saldos de wallet
   */
  downloadWalletBalancesExcel(
    query?: WalletBalancesQueryDto,
    filename?: string,
  ): void {
    this.exportWalletBalancesToExcel(query).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download =
          filename ||
          `saldos-usuarios-${new Date().toISOString().split('T')[0]}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
      },
    });
  }

  // ========================
  // MÉTODOS DE OPERACIONES DIARIAS
  // ========================

  /**
   * Obtener resumen de operaciones diarias
   * @param date Fecha en formato YYYY-MM-DD (opcional, por defecto hoy)
   */
  getDailyOperationsSummary(date?: string): Observable<DailyOperationsSummary> {
    this.loadingSubject.next(true);
    let params = new HttpParams();

    if (date) {
      params = params.set('date', date);
    }

    return this.http
      .get<DailyOperationsSummary>(`${this.apiUrl}/operations/daily-summary`, {
        headers: this.getHeaders(),
        params,
      })
      .pipe(
        catchError(this.handleError),
        finalize(() => this.loadingSubject.next(false)),
      );
  }

  /**
   * Obtener retiros pendientes de aprobación
   * Solo retorna count y totalAmount
   */
  getPendingWithdrawalsForApproval(): Observable<PendingWithdrawalsForApproval> {
    this.loadingSubject.next(true);

    return this.http
      .get<PendingWithdrawalsForApproval>(
        `${this.apiUrl}/operations/pending-withdrawals`,
        {
          headers: this.getHeaders(),
        }
      )
      .pipe(
        catchError(this.handleError),
        finalize(() => this.loadingSubject.next(false)),
      );
  }

}
